import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { HandledError } from "@context-providers/error-handler/error-handler-types";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { removeOne, setOne } from "@store/errors/errors-slice";
import { ErrorHandlerContext } from "@context-providers/error-handler/error-handler-context";
import { runtimeConfig } from "@src/runtime-config";
import { ErrorPage } from "@pages/error-page";
import { errorsSelector } from "@store/errors/errors-selectors";
import { useToast } from "@flat-ui";
import { sentryCaptureError } from "@utils/sentry-utils";
import { ErrorDetailMessage } from "@context-providers/error-handler/error-detail-message";

export function ErrorHandlerProvider({
  children,
}: PropsWithChildren): JSX.Element {
  const dispatch = useAppDispatch();
  const { openToast } = useToast();
  const errors = useAppSelector(errorsSelector);
  const errorsCount = useRef<number>(errors.length);
  const [pageError, setPageError] = useState<HandledError | null>(null);

  /** Logs error to Sentry */
  const logError = useCallback((error: HandledError): void => {
    if (!runtimeConfig.features.sentryDSNKey) {
      return;
    }

    const title = error.title;
    const baseError = error.error;

    sentryCaptureError({
      error: baseError,
      title,
    });
  }, []);

  /** Shows an error toast */
  const showErrorToast = useCallback(
    (error: HandledError): void => {
      openToast({
        key: error.id,
        title: error.title,
        message: <ErrorDetailMessage error={error.error} />,
        variant: "error",
      });
    },
    [openToast]
  );

  /**
   * Detects if there are new errors in the store.
   * If there are selects the latest error and:
   * - it logs it to sentry
   * - it removes it from the store
   * - if it's a "toast" error it shows the toast
   * - if it's a "page" error it sets it in the state
   */
  useEffect(() => {
    if (errors.length > errorsCount.current) {
      const newError = errors[errors.length - 1];
      logError(newError);
      dispatch(removeOne(newError.id));
      newError.uiType === "toast" && showErrorToast(newError);
      newError.uiType === "page" && setPageError(newError);
    }

    errorsCount.current = errors.length;
  }, [dispatch, errors, logError, showErrorToast]);

  /** Adds error to the store */
  const handleError = useCallback(
    (error: HandledError): void => {
      dispatch(setOne(error));
    },
    [dispatch]
  );

  return (
    <ErrorHandlerContext.Provider
      value={{
        handleError,
      }}
    >
      {pageError ? (
        <ErrorPage error={pageError} close={() => setPageError(null)} />
      ) : (
        children
      )}
    </ErrorHandlerContext.Provider>
  );
}
